// ============================================================
//   Contract Addresses — Split architecture
//   AdrenalinePlan  = write ops + admin + plan config
//   UserStorage     = user data + income tracking + matrix
//   NFTEngine       = NFT data + buckets
//
//   Only the PLAN address needs to be set manually.
//   USER_STORAGE and NFT_ENGINE are discovered at runtime via
//   planContract.userStorage() and planContract.nftEngine().
// ============================================================
export const CONTRACT_ADDRESSES = {
  OPBNB_MAINNET: {
    ADRENALINE_PLAN: '0xa8ec849D7043ae286bb12c0EadBCF674Bd7BaB64',
    USDT: '0x9e5AAC1Ba1a2e6aEd6b32689DFcF62A509Ca96f3'
  },
  OPBNB_TESTNET: {
    ADRENALINE_PLAN: '0x55cCDC1F6E767A5eFB6Ae7d9246a151C84f625Df',
    USDT: '0xBf6596a279172D330b8b732D257Ee633B6FDA911'
  }
};

// Default referrer address (admin)
export const DEFAULT_REFERRER = '0xBf3aD7aA0E011C1279568B7Ec25b303b32C2b1EE';

// Telegram API URL
export const TELEGRAM_API_URL = 'https://adrenalinenft.org/api/telegram.php';
export const TELEGRAM_BOT_USERNAME = '@adrenalinenft_bot';

// ============================================================
//   ERC20 ABI for USDT
// ============================================================
export const ERC20_ABI = [
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function balanceOf(address account) external view returns (uint256)",
  "function decimals() external view returns (uint8)",
  "function symbol() external view returns (string)"
];

// ============================================================
//   AdrenalinePlan ABI  (write ops + plan config)
// ============================================================
export const ADRENALINE_PLAN_ABI = [
  // ---- User write functions ----
  "function purchaseNFTs(uint256 amount, address sponsor, bool autoMode, bool normalMode) external",
  "function repurchaseNFTs(uint256 amount) external",
  "function withdrawIncome() external",

  // ---- Contract discovery ----
  "function usdtToken() external view returns (address)",
  "function userStorage() external view returns (address)",
  "function nftEngine() external view returns (address)",

  // ---- Admin config (read) ----
  "function owner() external view returns (address)",
  "function admin() external view returns (address)",
  "function companyWallet1() external view returns (address)",
  "function companyWallet2() external view returns (address)",
  "function devWallet() external view returns (address)",
  "function teamWallet1() external view returns (address)",
  "function teamWallet2() external view returns (address)",
  "function maxNFTsPerPurchase() external view returns (uint256)",
  "function normalModeEnabled() external view returns (bool)",
  "function maxAutoInvestment() external view returns (uint256)",
  "function maxNormalInvestment() external view returns (uint256)",
  "function PROFIT_CEILING() external view returns (uint256)",
  "function company1Percent() external view returns (uint256)",
  "function company2Percent() external view returns (uint256)",
  "function devPercent() external view returns (uint256)",
  "function autoProfitPercent() external view returns (uint256)",
  "function normalProfitPercent() external view returns (uint256)",
  "function sponsorPercent() external view returns (uint256)",
  "function levelPercentL1to15() external view returns (uint256)",
  "function levelPercentL16to30() external view returns (uint256)",
  "function autoReferralBonusPercent() external view returns (uint256)",
  "function eligibilityPeriod() external view returns (uint256)",
  "function eligibilityMinAutoReferrals() external view returns (uint256)",
  "function eligibilityMinVolume() external view returns (uint256)",
  "function directAutoBusinessRequiredL1to15() external view returns (uint256)",
  "function directAutoBusinessRequiredL16to30() external view returns (uint256)",
  "function MATRIX_LEVELS() external view returns (uint256)",

  // ---- Plan views ----
  "function getContractBalance() external view returns (uint256)",
  "function canUserPurchase(address user, bool isAuto) external view returns (bool, string)",

  // ---- Admin writes ----
  "function claimCapitalPool(uint256 amount) external",
  "function adminPurchaseByAmount(uint256 amount) external",
  "function adminBuyNFT(uint256 nftId) external",
  "function adminBurnNFT(uint256 nftId) external",
  "function setMaxNFTsPerPurchase(uint256 _max) external",
  "function setNormalModeEnabled(bool enabled) external",
  "function setMaxAutoInvestment(uint256 amount) external",
  "function setMaxNormalInvestment(uint256 amount) external",
  "function setWalletPercentages(uint256 _company1, uint256 _company2, uint256 _dev, uint256 _autoProfit, uint256 _normalProfit, uint256 _sponsor, uint256 _levelL1to15, uint256 _levelL16to30) external",
  "function setAutoReferralBonusPercent(uint256 _percent) external",
  "function setEligibilityParams(uint256 _period, uint256 _minAutoReferrals, uint256 _minVolume, uint256 _directAutoBusinessL1to15, uint256 _directAutoBusinessL16to30) external",
  "function updateWallets(address _company1, address _company2, address _dev, address _team1, address _team2) external",
  "function enableBucket(uint8 tier) external",
  "function disableBucket(uint8 tier) external",
  "function setBucketStatus(uint8 tier, bool enabled) external",
  "function changeAdmin(address newAdmin) external",
  "function transferOwnership(address newOwner) external",
  "function emergencyWithdraw(uint256 amount) external",
  "function setNFTEngine(address _newEngine) external",
  "function setUserStorage(address _newStorage) external",
  "function setNFTHoldTime(uint256 _seconds) external",
  "function setMaxBucketScanLimit(uint256 _limit) external",
  "function adminFixAutoInvestment(address user, uint256 correctAmount) external",
  "function adminFixPendingReinvest(address user, uint256 amount) external",

  // ---- Events (emitted by Plan) ----
  "event ActivityLog(address indexed user, string activityType, uint256 amount, uint256 timestamp)",
  "event IncomeDistributed(address indexed user, string incomeType, uint256 amount, uint256 timestamp)",
  "event IncomeMissed(address indexed user, string incomeType, uint256 amount, uint256 timestamp)",
  "event PrincipalReturned(address indexed user, uint256 amount, uint256 timestamp)",
  "event PendingReinvest(address indexed user, uint256 amount, uint256 timestamp)",
  "event AutoReinvest(address indexed user, uint256 amount, uint256 timestamp)",
  "event RefundIssued(address indexed user, uint256 amount, uint256 timestamp)",
  "event ProfitCeilingReached(address indexed user, uint256 timestamp)",
  "event Withdrawn(address indexed user, string incomeType, uint256 amount, uint256 timestamp)",
  "event PoolClaimed(string poolType, uint256 amount, uint256 timestamp)",
  "event AdminChanged(address indexed oldAdmin, address indexed newAdmin, uint256 timestamp)",
  "event OwnershipTransferred(address indexed previousOwner, address indexed newOwner, uint256 timestamp)",
  "event TeamPoolDistributed(uint256 amount, uint256 timestamp)",
  "event AutoReferralBonus(address indexed sponsor, address indexed user, uint256 amount, uint256 timestamp)"
];

// ============================================================
//   UserStorage ABI  (user data + income tracking)
// ============================================================
export const USER_STORAGE_ABI = [
  // ---- User registration / view ----
  "function isUserRegistered(address user) external view returns (bool)",
  "function hasHitCeiling(address user) external view returns (bool)",
  "function totalUsers() external view returns (uint256)",
  "function userByIndex(uint256 index) external view returns (address)",

  // ---- Aggregated views ----
  "function getUserDetails(address user) external view returns (address sponsor, uint256 totalInvested, uint256 totalEarned, uint256 principalBalance, uint256 pendingReinvest, uint256 autoInvested, uint256 normalInvested, uint256 directReferrals, uint256 directAutoUserBusiness, uint256 totalNFTsPurchased, bool isAutoUser, bool isNormalUser, bool isActive)",
  "function getUserIncomeBreakdown(address user) external view returns (uint256 sponsorIncome, uint256 levelIncome, uint256 profitIncome, uint256 autoReferralIncome, uint256 totalEarned)",
  "function getUserTeamStats(address user) external view returns (uint256 teamCount, uint256 teamBusiness, uint256 directCount, uint256 directAutoCount)",
  "function getClaimableIncome(address user) external view returns (uint256 sponsor, uint256 level, uint256 autoRef, uint256 total)",
  "function getMissedIncome(address user) external view returns (uint256 sponsor, uint256 level, uint256 total)",
  "function getLevelStats(address user, uint256 level) external view returns (uint256 userCount, uint256 business)",
  "function getAllLevelStats(address user) external view returns (uint256[] userCounts, uint256[] businesses)",
  "function getMatrixDownline(address user) external view returns (address[])",

  // ---- Raw state ----
  "function matrixUpline(address user) external view returns (address)",
  "function totalTeamBusiness(address user) external view returns (uint256)",
  "function capitalPool() external view returns (uint256)",
  "function getPendingUsersCount() external view returns (uint256)",
  "function getPendingUser(uint256 index) external view returns (address)",

  // ---- Eligibility ----
  "function isEligibleForLevel(address user) external view returns (bool)",
  "function eligibilityStartTime(address user) external view returns (uint256)",
  "function eligibilityAutoReferrals(address user) external view returns (uint256)",
  "function eligibilityAutoVolume(address user) external view returns (uint256)",
  "function wasEverEligible(address user) external view returns (bool)",

  // ---- Raw missed income (view-only) ----
  "function missedSponsorIncome(address user) external view returns (uint256)",
  "function missedLevelIncome(address user) external view returns (uint256)",
  "function claimableSponsorIncome(address user) external view returns (uint256)",
  "function claimableLevelIncome(address user) external view returns (uint256)",
  "function claimableAutoReferralIncome(address user) external view returns (uint256)",

  "function MATRIX_LEVELS() external view returns (uint256)",

  // ---- Investment deduction setters (onlyAuthorized — called by Plan when principal returns) ----
  "function deductNormalInvestment(address user, uint256 amount) external",
  "function deductAutoInvestment(address user, uint256 amount) external",

  // ---- Events (emitted by UserStorage) ----
  "event UserRegistered(address indexed user, address indexed sponsor, uint256 indexed serialNumber, uint256 timestamp)",
  "event MatrixPlacement(address indexed user, address indexed upline, uint256 position, uint256 timestamp)",
  "event TeamUpdated(address indexed user, uint256 level, uint256 teamCount, uint256 teamBusiness)"
];

// ============================================================
//   NFTEngine ABI  (NFT data + buckets)
// ============================================================
export const NFT_ENGINE_ABI = [
  // ---- NFT data ----
  "function nfts(uint256 nftId) external view returns (uint128 currentPrice, uint128 purchasePrice, address owner, uint64 lastPurchaseTime, uint32 nftId_, bool isActive, bool isAutoMode)",
  "function getNFTDetails(uint256 nftId) external view returns (uint256 currentPrice, uint256 purchasePrice, address nftOwner, uint256 lastPurchaseTime, bool isActive, bool isAutoMode)",
  "function getUserNFTs(address user, uint256 offset, uint256 limit) external view returns (uint256[] nftIds, uint256 total)",
  "function getNFTsByPrice(uint256 price, uint256 offset, uint256 limit) external view returns (uint256[] nftIds, uint256 total)",
  "function getAllActiveNFTsByPrice() external view returns (uint256[] prices, uint256[] counts)",
  "function findCheapestNFT(address skipOwner, uint8 bucketIndex, address adminAddr) external view returns (uint256 nftId, uint256 price)",
  "function detectStaleNFTs(uint256 staleHours, uint256 offset, uint256 limit) external view returns (uint256[] staleIds, uint256 total)",
  "function nextNFTId() external view returns (uint256)",

  // ---- Bucket management ----
  "function isBucketEnabled(uint8 tier) external view returns (bool)",
  "function getAllBucketStatus() external view returns (bool[9])",
  "function getBucketStartIndex(uint8 bucketIndex) external view returns (uint256)",
  "function nftHoldTime() external view returns (uint256)",
  "function maxBucketScanLimit() external view returns (uint256)",
  "function setNFTHoldTime(uint256 _seconds) external",
  "function setMaxBucketScanLimit(uint256 _limit) external",

  // ---- Events ----
  "event NFTPurchased(address indexed buyer, uint256 indexed nftId, uint256 price, uint256 timestamp)",
  "event NFTBurned(uint256 indexed nftId, uint256 timestamp)",
  "event NFTMinted(uint256 indexed nftId, uint256 price, address indexed nftOwner, uint256 timestamp)"
];

// Backwards-compat alias (some older imports may still use this)
export const ADRENALINE_NFT_ABI = ADRENALINE_PLAN_ABI;
